'use client';
import React, { useState } from 'react';
import { Search, MapPin, Info, Phone, X } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import PublicSearchResults from './PublicSearchResults';

export type PublicSearchResult = {
  id: string;
  fullName: string;
  idNumber: string;
  dateOfBirth: string;
  dateOfDeath: string;
  burialDate: string;
  cemetery: string;
  block: string;
  row: string;
  plotNumber: string;
  graveId: string;
  section: string;
};

const allDeceased: PublicSearchResult[] = [
  { id: 'pub-001', fullName: 'Thandi Nxumalo', idNumber: '5104152345678', dateOfBirth: '15 Apr 1951', dateOfDeath: '27 Jul 2026', burialDate: '02 Aug 2026', cemetery: 'Mthatha Cemetery', block: 'B2', row: 'R1', plotNumber: '009', graveId: 'NYD-MTH-B2-R1-009', section: 'General' },
  { id: 'pub-002', fullName: 'Bongani Ntanzi', idNumber: '5501126789012', dateOfBirth: '12 Jan 1955', dateOfDeath: '30 Jul 2026', burialDate: '04 Aug 2026', cemetery: 'Libode Cemetery', block: 'A2', row: 'R1', plotNumber: '007', graveId: 'NYD-LIB-A2-R1-007', section: 'General' },
  { id: 'pub-003', fullName: 'Lungelo Maphumulo', idNumber: '4809234512678', dateOfBirth: '23 Sep 1948', dateOfDeath: '28 Jul 2026', burialDate: '03 Aug 2026', cemetery: 'Ngqeleni Cemetery', block: 'A1', row: 'R2', plotNumber: '014', graveId: 'NYD-NGQ-A1-R2-014', section: 'General' },
  { id: 'pub-004', fullName: 'Nosipho Cele', idNumber: '5803074321098', dateOfBirth: '07 Mar 1958', dateOfDeath: '24 Jul 2026', burialDate: '01 Aug 2026', cemetery: 'Ngqeleni Cemetery', block: 'B1', row: 'R3', plotNumber: '011', graveId: 'NYD-NGQ-B1-R3-011', section: 'General' },
  { id: 'pub-005', fullName: 'Vusi Mkhize', idNumber: '4506085432109', dateOfBirth: '08 Jun 1945', dateOfDeath: '22 Jul 2026', burialDate: '30 Jul 2026', cemetery: 'Libode Cemetery', block: 'B2', row: 'R4', plotNumber: '027', graveId: 'NYD-LIB-B2-R4-027', section: 'General' },
  { id: 'pub-006', fullName: 'Nosipho Dlamini', idNumber: '6301175432109', dateOfBirth: '17 Jan 1963', dateOfDeath: '10 Jun 2026', burialDate: '16 Jun 2026', cemetery: 'Mthatha Cemetery', block: 'A3', row: 'R2', plotNumber: '021', graveId: 'NYD-MTH-A3-R2-021', section: 'General' },
  { id: 'pub-007', fullName: 'Mziwandile Gumbi', idNumber: '5209234512091', dateOfBirth: '23 Sep 1952', dateOfDeath: '05 May 2026', burialDate: '11 May 2026', cemetery: 'Mthatha Cemetery', block: 'C3', row: 'R1', plotNumber: '004', graveId: 'NYD-MTH-C3-R1-004', section: 'General' },
  { id: 'pub-008', fullName: 'Nokwanda Zulu', idNumber: '7803045678082', dateOfBirth: '04 Mar 1978', dateOfDeath: '01 Aug 2026', burialDate: '04 Aug 2026', cemetery: 'Mthatha Cemetery', block: 'B4', row: 'R3', plotNumber: '022', graveId: 'NYD-MTH-B4-R3-022', section: 'General' },
];

export default function PublicSearchPortal() {
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<PublicSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    // TODO: BACKEND — GET /api/public/grave-search?q={query}
    await new Promise((r) => setTimeout(r, 700));
    const q = query.toLowerCase();
    const found = allDeceased.filter(
      (d) =>
        d.fullName.toLowerCase().includes(q) ||
        d.idNumber.includes(q) ||
        d.graveId.toLowerCase().includes(q)
    );
    setResults(found);
    setSearched(true);
    setSearching(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearSearch = () => {
    setQuery('');
    setSearched(false);
    setResults([]);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Public Header */}
      <header className="bg-primary text-white">
        <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppLogo size={36} />
            <div>
              <span className="font-700 text-base text-white leading-tight block">Nyandeni Municipality</span>
              <span className="text-xs text-white/70 leading-tight block">Cemetery Services — Public Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:+27471234567" className="hidden sm:flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors">
              <Phone size={14} />
              +27 47 123 4567
            </a>
            <Link
              href="/"
              className="text-sm bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg transition-colors font-500"
            >
              Staff Login
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Search Section */}
      <div className="bg-primary pb-16 pt-12">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <MapPin size={20} className="text-accent" />
            <span className="text-white/70 text-sm font-500">Grave Locator Service</span>
          </div>
          <h1 className="text-3xl font-800 text-white mb-2">Find a Grave</h1>
          <p className="text-white/70 text-sm mb-8">
            Search by full name, South African ID number, or grave reference number across all Nyandeni cemeteries.
          </p>

          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter full name, ID number, or grave ID..."
                className="w-full pl-11 pr-10 py-3.5 rounded-xl border border-border bg-card text-foreground text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
              />
              {query && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <button
              onClick={handleSearch}
              disabled={searching || !query.trim()}
              className="btn-primary px-6 rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {searching ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Search size={16} />
              )}
              Search
            </button>
          </div>

          {/* Search hints */}
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-white/50">
            <span>Try: "Zulu"</span>
            <span>·</span>
            <span>Try: "5104152345678"</span>
            <span>·</span>
            <span>Try: "NYD-MTH"</span>
          </div>
        </div>
      </div>

      {/* Results / Content Area */}
      <div className="max-w-screen-xl mx-auto px-6 -mt-8 pb-16">
        {!searched ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-0">
            {/* Cemetery Cards */}
            {[
              { id: 'cem-card-mthatha', name: 'Mthatha Cemetery', plots: 2800, occupied: 2052, location: 'Mthatha, Eastern Cape' },
              { id: 'cem-card-libode', name: 'Libode Cemetery', plots: 1200, occupied: 748, location: 'Libode, Eastern Cape' },
              { id: 'cem-card-ngqeleni', name: 'Ngqeleni Cemetery', plots: 820, occupied: 414, location: 'Ngqeleni, Eastern Cape' },
            ].map((c) => (
              <div key={c.id} className="card-base p-5 shadow-card">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                    <MapPin size={18} className="text-primary" />
                  </div>
                  <span className="text-xs text-muted-foreground">{Math.round((c.occupied / c.plots) * 100)}% occupied</span>
                </div>
                <h3 className="text-base font-700 text-foreground">{c.name}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{c.location}</p>
                <div className="mt-3 w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${Math.round((c.occupied / c.plots) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {c.occupied.toLocaleString('en-ZA')} / {c.plots.toLocaleString('en-ZA')} plots
                </p>
              </div>
            ))}
          </div>
        ) : (
          <PublicSearchResults results={results} query={query} />
        )}

        {/* Info Notice */}
        <div className="mt-8 p-4 bg-[#f0f9ff] border border-[#bae6fd] rounded-xl flex items-start gap-3">
          <Info size={16} className="text-[#0369a1] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-600 text-[#0369a1]">Requesting Certified Documents</p>
            <p className="text-xs text-[#0369a1]/80 mt-0.5">
              To obtain a certified burial certificate or official grave location confirmation, please visit the Nyandeni Municipality offices at 1 Main Street, Mthatha, or call +27 47 123 4567 during office hours (Mon–Fri, 08:00–16:00).
            </p>
          </div>
        </div>
      </div>

      {/* Public Footer */}
      <footer className="bg-foreground text-white/70 py-8">
        <div className="max-w-screen-xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AppLogo size={28} />
            <div>
              <p className="text-sm font-600 text-white">Nyandeni Local Municipality</p>
              <p className="text-xs">Cemetery Management System</p>
            </div>
          </div>
          <p className="text-xs text-center">
            © 2026 Nyandeni Local Municipality. All rights reserved. · Cemetery Services Department
          </p>
          <div className="flex items-center gap-4 text-xs">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors">Terms of Use</a>
            <a href="tel:+27471234567" className="hover:text-white transition-colors">+27 47 123 4567</a>
          </div>
        </div>
      </footer>
    </div>
  );
}